import React from 'react';
import { useLanguage } from '@/contexts/language/LanguageContext';
import { blogProblemWithEcommerceCopy } from '@/contexts/language/translations/pages/blogProblemWithEcommerce';

// Illustrative journey from revenue to profit, in % of revenue.
// Labels and texts (EN + SK) live in src/contexts/language/translations/pages/blogProblemWithEcommerce.ts
const JOURNEY_NUMBERS = [
  { cost: 0, left: 100 },
  { cost: 65, left: 35 },
  { cost: 12, left: 23 },
  { cost: 7, left: 16 },
  { cost: 1, left: 15 },
  { cost: 5, left: 10 },
];

const TheProblemWithEcommerce = () => {
  const { language } = useLanguage();
  const c = blogProblemWithEcommerceCopy[language];
  const JOURNEY = JOURNEY_NUMBERS.map((n, i) => ({ ...n, ...c.journey[i] }));

  return (
  <>
    {/* Stats bar */}
    <section className="bg-[#2A1E47] py-10">
      <div className="container-custom">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 max-w-5xl mx-auto">
          {c.stats.map(stat => (
            <div key={stat.value} className="text-center">
              <p className="text-3xl md:text-4xl font-bold text-[#FF6B35] mb-1">{stat.value}</p>
              <p className="text-xs text-white/60 font-medium">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Body */}
    <section className="py-16 md:py-24 bg-white">
      <div className="container-custom">
        <div className="max-w-3xl mx-auto space-y-16 reveal">

          {/* The dream */}
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF6B35] mb-3">{c.dream.eyebrow}</p>
            <h2 className="text-2xl md:text-3xl font-bold text-[#2A1E47] mb-5">
              {c.dream.title}
            </h2>
            <p className="text-[#2A1E47]/70 leading-relaxed text-lg mb-4">
              {c.dream.p1}
            </p>
            <p className="text-[#2A1E47]/70 leading-relaxed text-lg mb-4">
              {c.dream.p2}
            </p>
            <p className="text-[#2A1E47]/70 leading-relaxed text-lg">
              {c.dream.p3}
            </p>
          </div>

          {/* The reality */}
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF6B35] mb-3">{c.reality.eyebrow}</p>
            <h2 className="text-2xl md:text-3xl font-bold text-[#2A1E47] mb-5">
              {c.reality.title}
            </h2>
            <p className="text-[#2A1E47]/70 leading-relaxed text-lg mb-4">
              {c.reality.p1}
            </p>
            <p className="text-[#2A1E47]/70 leading-relaxed text-lg">
              {c.reality.p2}
            </p>
            <blockquote className="border-l-4 border-[#FF6B35] pl-6 py-2 my-8">
              <p className="text-xl italic text-[#2A1E47]/80 leading-relaxed">
                {c.reality.quote}
              </p>
            </blockquote>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="bg-[#FAFAFA] rounded-2xl p-6 md:p-8">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#2A1E47]/40 mb-2">{c.reality.friendLabel}</p>
                <p className="text-2xl font-bold text-[#2A1E47]">{c.reality.friend}</p>
              </div>
              <div className="bg-[#FFF7EC] rounded-2xl p-6 md:p-8">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#2A1E47]/40 mb-2">{c.reality.enemyLabel}</p>
                <p className="text-2xl font-bold text-[#FF6B35]">{c.reality.enemy}</p>
              </div>
            </div>

            <p className="text-[#2A1E47]/70 leading-relaxed text-lg mt-8 mb-4">
              {c.reality.p3}
            </p>
            <p className="text-[#2A1E47]/70 leading-relaxed text-lg">
              {c.reality.p4Before}<strong className="text-[#2A1E47]">{c.reality.p4Discipline}</strong>{c.reality.p4And}<strong className="text-[#2A1E47]">{c.reality.p4Data}</strong>{c.reality.p4After}
            </p>
          </div>

          {/* Journey to profit */}
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF6B35] mb-3">{c.journeySection.eyebrow}</p>
            <h2 className="text-2xl md:text-3xl font-bold text-[#2A1E47] mb-5">
              {c.journeySection.title}
            </h2>
            <p className="text-[#2A1E47]/70 leading-relaxed text-lg mb-8">
              {c.journeySection.p1}
            </p>

            <div className="bg-[#FAFAFA] rounded-2xl p-6 md:p-10 space-y-7">
              {JOURNEY.map(step => (
                <div key={step.label}>
                  <div className="flex items-baseline justify-between gap-4 mb-2">
                    <p className="font-bold text-[#2A1E47]">{step.label}</p>
                    <p className="text-sm font-semibold whitespace-nowrap">
                      {step.cost > 0 && <span className="text-[#FF6B35] mr-3">−{c.percent(step.cost)}</span>}
                      <span className="text-[#2A1E47]">{step.cost > 0 ? c.percentLeft(step.left) : c.percent(step.left)}</span>
                    </p>
                  </div>
                  <div className="relative h-3 rounded-full bg-white border border-gray-200 overflow-hidden mb-2">
                    <div className="absolute inset-y-0 left-0 bg-[#2A1E47] rounded-full" style={{ width: `${step.left}%` }} />
                    {step.cost > 0 && (
                      <div className="absolute inset-y-0 bg-[#FF6B35]/30" style={{ left: `${step.left}%`, width: `${step.cost}%` }} />
                    )}
                  </div>
                  <p className="text-sm text-[#2A1E47]/60 leading-relaxed">{step.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* OPEX callout */}
          <div className="bg-[#2A1E47] rounded-2xl p-8 md:p-10 text-white">
            <h2 className="text-xl md:text-2xl font-bold mb-4">{c.opex.title}</h2>
            <p className="text-white/70 leading-relaxed text-lg mb-4">
              {c.opex.p1}
            </p>
            <p className="text-white leading-relaxed text-lg font-semibold">
              {c.opex.p2}
            </p>
          </div>

          {/* The real risk */}
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF6B35] mb-3">{c.risk.eyebrow}</p>
            <h2 className="text-2xl md:text-3xl font-bold text-[#2A1E47] mb-5">
              {c.risk.title}
            </h2>
            <p className="text-[#2A1E47]/70 leading-relaxed text-lg mb-4">
              {c.risk.p1}
            </p>
            <p className="text-[#2A1E47]/70 leading-relaxed text-lg">
              {c.risk.p2}
            </p>
          </div>

          {/* How we approach it */}
          <div className="bg-[#FAFAFA] rounded-2xl p-8 md:p-10">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF6B35] mb-3">{c.approach.eyebrow}</p>
            <h2 className="text-xl md:text-2xl font-bold text-[#2A1E47] mb-6">{c.approach.title}</h2>
            <div className="space-y-4">
              {c.steps.map((step, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-6 h-6 rounded-full bg-[#2A1E47] text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                    {i + 1}
                  </div>
                  <p className="text-[#2A1E47]/80 leading-relaxed">{step}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Why it matters */}
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF6B35] mb-3">{c.why.eyebrow}</p>
            <h2 className="text-2xl md:text-3xl font-bold text-[#2A1E47] mb-5">
              {c.why.title}
            </h2>
            <p className="text-[#2A1E47]/70 leading-relaxed text-lg">
              {c.why.p1}
            </p>
          </div>

        </div>
      </div>
    </section>
  </>
  );
};

export default TheProblemWithEcommerce;
